// markdown-body.jsx — the JSX rendering layer for the shared legal-page
// renderer. Consumes the pure token stream from markdown-tokenizer.js and
// turns it into React elements. Extracted from App.jsx (2026-06-15).
//
// SECURITY: React-element based ONLY. Text becomes React children (auto-
// escaped); link hrefs go through safeMdLink(). There is intentionally NO
// dangerouslySetInnerHTML here — never introduce one (a source-guard test
// enforces this). The DOMPurify/SafeHtml path in App.jsx is a separate
// mechanism for /blog + /prohibited-items and is unrelated to this renderer.
//
// PHASE 1 (extraction): renders 'br' | 'h2' | 'h3' | 'p' exactly as the old
// inline App.jsx MarkdownBody did — byte-identical output.

import { tokenizeMarkdown, safeMdLink } from "./markdown-tokenizer.js";

export { safeMdLink };

// Inline parsing of **bold** and [text](url). Shared helper (reused by
// paragraphs now, and by list items / table cells in Phase 2). Returns an
// array of React nodes; plain segments stay as auto-escaped strings.
function renderInline(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} style={{ color: "var(--ink)" }}>{part.slice(2, -2)}</strong>;
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return <a key={i} href={safeMdLink(linkMatch[2])} style={{ color: "var(--bl)" }}>{linkMatch[1]}</a>;
    }
    return part;
  });
}

export function MarkdownBody({ text }) {
  if (!text) return null;
  const tokens = tokenizeMarkdown(text);
  let key = 0;
  const elements = tokens.map((t) => {
    if (t.type === 'br') return <br key={key++} />;
    if (t.type === 'h2') return <h2 key={key++} style={{ fontWeight: 700, fontSize: 18, color: "var(--ink)", margin: "28px 0 12px" }}>{t.text}</h2>;
    if (t.type === 'h3') return <h3 key={key++} style={{ fontWeight: 700, fontSize: 16, color: "var(--ink)", margin: "20px 0 8px" }}>{t.text}</h3>;
    if (t.type === 'hr') return <hr key={key++} style={{ border: "none", borderTop: "1px solid var(--bd)", margin: "24px 0" }} />;
    if (t.type === 'ul') {
      return (
        <ul key={key++} style={{ margin: "0 0 16px", paddingLeft: 22 }}>
          {t.items.map((it, j) => <li key={j} style={{ marginBottom: 6, lineHeight: 1.7 }}>{renderInline(it)}</li>)}
        </ul>
      );
    }
    if (t.type === 'table') {
      return (
        <div key={key++} style={{ overflowX: "auto", margin: "12px 0 20px" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
            <thead>
              <tr>
                {t.header.map((h, j) => (
                  <th key={j} style={{ textAlign: "left", padding: "8px 10px", borderBottom: "2px solid var(--bd)", fontWeight: 700, color: "var(--ink)", verticalAlign: "top" }}>{renderInline(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {t.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((c, j) => (
                    <td key={j} style={{ padding: "8px 10px", borderBottom: "1px solid var(--bd)", verticalAlign: "top" }}>{renderInline(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return <p key={key++} style={{ marginBottom: 16 }}>{renderInline(t.text)}</p>;
  });
  return <>{elements}</>;
}
